import React, { useState } from "react";
import {
  WorkflowRunStepOperation,
  AgentWorkflowRunStepOperationType,
  TriggerOperationParameters,
} from "@/store/workflowRunStore";
import { ChevronDown, ChevronUp, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAgentWorkflowTriggersStore } from "@/store/agentWorkflowTriggersStore";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatJson } from "@/modules/agents/components/helpers/jsonUtils";

interface WorkflowRunViewTriggerProps {
  id: string;
  operation: WorkflowRunStepOperation;
}

export const WorkflowRunViewTrigger: React.FC<WorkflowRunViewTriggerProps> = ({
  operation,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { getTriggerById } = useAgentWorkflowTriggersStore();

  if (
    operation.operationParameters.type !==
    AgentWorkflowRunStepOperationType.TRIGGER
  ) {
    return null;
  }

  const params = operation.operationParameters as TriggerOperationParameters;
  const triggerId = params.triggerId;
  const trigger = triggerId ? getTriggerById(triggerId) : undefined;

  const triggerName = trigger?.name || "Unknown Trigger";
  const formattedOutput = formatJson(params.output);

  return (
    <div className="pl-11">
      <div className="border rounded-lg p-3 space-y-2 group transition-all duration-200 ease-in-out w-full">
        <div className="flex flex-col gap-2">
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="flex items-center gap-2">
              <span className={cn("flex items-center gap-2")}>
                <span className="text-purple-500">
                  <Zap className="h-4 w-4" />
                </span>
                <span className="text-sm font-medium">{triggerName}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </div>
          </div>
        </div>

        <div
          className={`mt-3 space-y-3 ${isExpanded ? "max-h-[600px]" : "max-h-0"} transition-all duration-200 ease-in-out w-full overflow-hidden`}
        >
          <ScrollArea className="max-h-[600px] overflow-y-auto">
            {formattedOutput && (
              <div className="space-y-2">
                <div className="text-xs bg-muted p-2 rounded overflow-auto max-h-48 max-w-full whitespace-pre-wrap font-mono">
                  {JSON.stringify(formattedOutput, null, 2)}
                </div>
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};
